import { useCallback, useEffect, useMemo, useState } from 'react';
import { createElement } from 'react';
import { Badge } from '@/components/ui/badge';
import { formatCurrency, formatDate } from '@/lib/utils';
import type { DataTableColumn } from '@/shared/components/data-table';
import { PhotoCell } from '../components/skus/PhotoCell';
import type { Sku, SkuType } from '../types';

const VISIBILITY_KEY = 'atek-skus-column-visibility';
const FILTERS_KEY = 'atek-skus-column-filters';

export interface SkuRenderContext {
  inventoryTotals: Record<string, { quantityOnHand: number }>;
  inventoryLoading: boolean;
}

export type SkuColumnDef = DataTableColumn<Sku, SkuRenderContext>;

const HIDDEN_BY_DEFAULT = new Set<string>(['tags', 'created_at']);

function getTypeBadgeClass(type: SkuType): string {
  switch (type) {
    case 'inventory':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'non_inventory':
      return 'bg-amber-100 text-amber-800 border-amber-200';
    case 'service':
      return 'bg-purple-100 text-purple-800 border-purple-200';
    default:
      return '';
  }
}

function getTypeLabel(type: SkuType): string {
  if (type === 'inventory') return 'Inventory';
  if (type === 'non_inventory') return 'Non-Inventory';
  if (type === 'service') return 'Service';
  return type;
}

function buildColumns(): SkuColumnDef[] {
  return [
    {
      key: 'photos',
      label: 'Photos',
      noFilter: true,
      getValue: (s) => String(s.photos?.length ?? 0),
      render: (s) =>
        createElement(PhotoCell, {
          photos: s.photos ?? [],
          orgId: s.organisationId,
          skuId: s.id,
        }),
    },
    {
      key: 'sku',
      label: 'SKU',
      sortKey: 'sku',
      filterType: 'text',
      isPrimarySearch: true,
      searchPlaceholder: 'Search SKU...',
      getValue: (s) => s.sku,
      render: (s) => createElement('span', { className: 'font-mono text-sm' }, s.sku),
    },
    {
      key: 'type',
      label: 'Type',
      sortKey: 'type',
      filterType: 'select',
      filterAllLabel: 'All Types',
      filterOptions: [
        { label: 'Inventory', value: 'inventory' },
        { label: 'Non-Inventory', value: 'non_inventory' },
        { label: 'Service', value: 'service' },
      ],
      getValue: (s) => s.type,
      render: (s) =>
        createElement(
          Badge,
          { variant: 'outline', className: getTypeBadgeClass(s.type) },
          getTypeLabel(s.type),
        ),
    },
    {
      key: 'name_en',
      label: 'English Name',
      sortKey: 'nameEn',
      filterType: 'text',
      getValue: (s) => s.nameEn,
      className: 'max-w-[200px] truncate',
    },
    {
      key: 'name_fr',
      label: 'French Name',
      sortKey: 'nameFr',
      filterType: 'text',
      getValue: (s) => s.nameFr,
      className: 'max-w-[200px] truncate',
    },
    {
      key: 'manufacturer',
      label: 'Manufacturer',
      sortKey: 'manufacturerName',
      filterType: 'text',
      getValue: (s) => s.manufacturerName ?? '',
    },
    {
      key: 'model',
      label: 'Model',
      filterType: 'text',
      getValue: (s) => s.model ?? '',
    },
    {
      key: 'activated',
      label: 'Active',
      filterType: 'select',
      filterAllLabel: 'All',
      filterOptions: [
        { label: 'Yes', value: 'true' },
        { label: 'No', value: 'false' },
      ],
      getValue: (s) => (s.activated ? 'true' : 'false'),
      render: (s) =>
        createElement(
          Badge,
          { variant: s.activated ? 'default' : 'secondary' },
          s.activated ? 'Yes' : 'No',
        ),
    },
    {
      key: 'saleable',
      label: 'Saleable',
      filterType: 'select',
      filterAllLabel: 'All',
      filterOptions: [
        { label: 'Yes', value: 'true' },
        { label: 'No', value: 'false' },
      ],
      getValue: (s) => (s.saleable ? 'true' : 'false'),
      render: (s) =>
        createElement(
          Badge,
          { variant: s.saleable ? 'default' : 'secondary' },
          s.saleable ? 'Yes' : 'No',
        ),
    },
    {
      key: 'value',
      label: 'Price',
      sortKey: 'value',
      className: 'text-right tabular-nums',
      getValue: (s) => s.value ?? '',
      render: (s) => formatCurrency(s.value),
    },
    {
      key: 'cost',
      label: 'Cost',
      sortKey: 'cost',
      className: 'text-right tabular-nums',
      getValue: (s) => s.cost ?? '',
      render: (s) => formatCurrency(s.cost),
    },
    {
      key: 'inventory',
      label: 'Inventory',
      className: 'text-right tabular-nums',
      getValue: (s, ctx) => {
        if (!s.mongoId) return '';
        const t = ctx.inventoryTotals[s.mongoId];
        return t ? String(t.quantityOnHand) : '';
      },
      render: (s, ctx) => {
        if (ctx.inventoryLoading) {
          return createElement('span', { className: 'text-muted-foreground' }, '…');
        }
        if (!s.mongoId) {
          return createElement('span', { className: 'text-muted-foreground' }, '—');
        }
        const t = ctx.inventoryTotals[s.mongoId];
        return t !== undefined
          ? String(t.quantityOnHand)
          : createElement('span', { className: 'text-muted-foreground' }, '—');
      },
    },
    {
      key: 'minstock',
      label: 'Min Stock',
      className: 'text-right tabular-nums',
      getValue: (s) => (s.minstock != null ? String(s.minstock) : ''),
    },
    {
      key: 'tags',
      label: 'Tags',
      filterType: 'text',
      getValue: (s) => (s.tags ?? []).join(', '),
    },
    {
      key: 'created_at',
      label: 'Created',
      sortKey: 'createdAt',
      getValue: (s) => s.createdAt ?? '',
      render: (s) =>
        createElement(
          'span',
          { className: 'text-muted-foreground text-sm' },
          formatDate(s.createdAt),
        ),
    },
  ];
}

function getDefaultVisibility(columns: SkuColumnDef[]): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  for (const col of columns) out[col.key] = !HIDDEN_BY_DEFAULT.has(col.key);
  return out;
}

function loadVisibility(columns: SkuColumnDef[]): Record<string, boolean> {
  const defaults = getDefaultVisibility(columns);
  try {
    const stored = localStorage.getItem(VISIBILITY_KEY);
    if (stored) return { ...defaults, ...JSON.parse(stored) };
  } catch {
    /* ignore */
  }
  return defaults;
}

function loadFilters(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(FILTERS_KEY) || '{}');
  } catch {
    return {};
  }
}

export function useSkuListTable(
  skus: Sku[],
  inventoryTotals: Record<string, { quantityOnHand: number }> = {},
  inventoryLoading = false,
) {
  const columns = useMemo(() => buildColumns(), []);
  const ctx: SkuRenderContext = useMemo(
    () => ({ inventoryTotals, inventoryLoading }),
    [inventoryTotals, inventoryLoading],
  );

  const [visibility, setVisibility] = useState<Record<string, boolean>>(() =>
    loadVisibility(columns),
  );
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(loadFilters);
  const [sortKey, setSortKey] = useState<string>('sku');
  const [sortDirection, setSortDirection] = useState<1 | -1>(1);

  useEffect(() => {
    localStorage.setItem(VISIBILITY_KEY, JSON.stringify(visibility));
  }, [visibility]);

  useEffect(() => {
    localStorage.setItem(FILTERS_KEY, JSON.stringify(columnFilters));
  }, [columnFilters]);

  const isVisible = useCallback((key: string) => visibility[key] !== false, [visibility]);

  const visibleColumns = useMemo(
    () => columns.filter((c) => isVisible(c.key)),
    [columns, isVisible],
  );

  const toggleColumn = useCallback((key: string) => {
    setVisibility((prev) => ({ ...prev, [key]: !(prev[key] !== false) }));
  }, []);

  const setColumnFilter = useCallback((key: string, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSort = useCallback((key: string) => {
    setSortKey((prevKey) => {
      if (prevKey === key) {
        setSortDirection((d) => (d === 1 ? -1 : 1));
        return prevKey;
      }
      setSortDirection(1);
      return key;
    });
  }, []);

  const filteredSkus = useMemo(() => {
    const active = Object.entries(columnFilters).filter(([, v]) => v);
    if (active.length === 0) return skus;
    return skus.filter((sku) =>
      active.every(([colKey, value]) => {
        const col = columns.find((c) => c.key === colKey);
        if (!col) return true;
        const raw = col.getValue(sku, ctx);

        if (col.filterType === 'select') {
          try {
            const selected = JSON.parse(value) as string[];
            return selected.length === 0 || selected.includes(String(raw ?? ''));
          } catch {
            return true;
          }
        }
        return String(raw ?? '')
          .toLowerCase()
          .includes(value.toLowerCase());
      }),
    );
  }, [skus, columnFilters, columns, ctx]);

  const sortedSkus = useMemo(() => {
    const col = columns.find((c) => c.sortKey === sortKey || c.key === sortKey);
    if (!col) return filteredSkus;
    const dir = sortDirection;
    return [...filteredSkus].sort((a, b) => {
      const va = col.getValue(a, ctx);
      const vb = col.getValue(b, ctx);
      // Numeric compare when both look like numbers
      const na = Number(va);
      const nb = Number(vb);
      if (!isNaN(na) && !isNaN(nb) && va !== '' && vb !== '') {
        if (na === nb) return 0;
        return na > nb ? dir : -dir;
      }
      if (va === vb) return 0;
      if (va == null || va === '') return 1;
      if (vb == null || vb === '') return -1;
      return va > vb ? dir : -dir;
    });
  }, [filteredSkus, sortKey, sortDirection, columns, ctx]);

  return {
    columns,
    visibleColumns,
    columnFilters,
    setColumnFilter,
    sortKey,
    sortDirection,
    handleSort,
    isVisible,
    toggleColumn,
    sortedSkus,
    ctx,
  };
}
